import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { LoaderCircle } from "lucide-react";
import { cn } from "../../lib/cn";

/**
 * Button — CTA laiton/or, variantes multiples. Style cohérent avec le thème steampunk.
 */

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "brass"
  | "ghost"
  | "danger"
  | "arcane"
  | "violet"
  | "link";
export type ButtonSize = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  fullWidth?: boolean;
}

const BASE =
  "relative inline-flex min-w-0 items-center justify-center gap-2 overflow-hidden rounded border font-body font-semibold tracking-wide transition-all duration-200 ease-brass focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-arcane-300/70 focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian-400 disabled:cursor-not-allowed disabled:opacity-50 select-none max-sm:min-h-10";

const VARIANTS: Record<ButtonVariant, string> = {
  // CTA principal : lingot de feuille d'or poli.
  primary: "border-arcane-300/70 bg-arcane-500 text-parchment-50 shadow-glow-arcane hover:bg-arcane-400 active:translate-y-px",
  secondary:
    "border-iron-300/60 bg-obsidian-100 text-parchment-100 hover:border-arcane-300/60 hover:bg-iron-600 active:translate-y-px",
  // Plaque de laiton martelée (action secondaire chaleureuse).
  brass: "border-bronze-300/60 bg-bronze-500 text-parchment-50 hover:bg-bronze-400 active:translate-y-px",
  ghost:
    "border-transparent text-parchment-200/80 hover:border-iron-300/40 hover:bg-iron-600/50 hover:text-parchment-100 active:translate-y-px",
  // Danger = sang forgé.
  danger: "border-blood-300/70 bg-blood-500 text-parchment-50 shadow-glow-blood hover:bg-blood-400 active:translate-y-px",
  // Arcane = teal (magie froide).
  arcane: "border-arcane-300/70 bg-arcane-500 text-parchment-50 shadow-glow-arcane hover:bg-arcane-400 active:translate-y-px",
  // Violet = braise arcanique.
  violet: "border-violet-300/70 bg-violet-500 text-white shadow-glow-violet hover:bg-violet-400 active:translate-y-px",
  link: "border-transparent px-0 text-arcane-200 underline-offset-4 hover:text-arcane-100 hover:underline overflow-visible",
};

const SIZES: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-xs",
  md: "h-10 px-4 text-sm",
  lg: "h-12 px-6 text-base",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    loading = false,
    disabled,
    iconLeft,
    iconRight,
    fullWidth,
    className,
    children,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(BASE, VARIANTS[variant], variant !== "link" && SIZES[size], fullWidth && "w-full", className)}
      {...rest}
    >
      {loading && <Spinner />}
      {!loading && iconLeft}
      <span className={cn("min-w-0 truncate", loading && "opacity-80")}>{children}</span>
      {!loading && iconRight}
    </button>
  );
});

function Spinner() {
  return <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden />;
}
